// src/components/ErrorBoundary.js
// Catches any unhandled render error in the React tree and shows a
// branded recovery screen instead of a blank white page.
//
// Usage in index.js:
//   <ErrorBoundary>
//     <AuthProvider><App /></AuthProvider>
//   </ErrorBoundary>

import React from "react";

const LogoMark = () => (
  <svg viewBox="0 0 80 80" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
    <polygon points="40,3 72,21 72,59 40,77 8,59 8,21" fill="none" stroke="#C9A227" strokeWidth="5"/>
    <polygon points="40,10 65,25 65,55 40,70 15,55 15,25" fill="#080C12" stroke="#2B5EA7" strokeWidth="1.5"/>
    <line x1="17" y1="40" x2="29" y2="40" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="51" y1="40" x2="63" y2="40" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="40" y1="13" x2="40" y2="25" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="40" y1="55" x2="40" y2="67" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="40" cy="40" r="14" fill="none" stroke="#2B5EA7" strokeWidth="1.5"/>
    <circle cx="40" cy="40" r="6" fill="#D93025"/>
    <polygon points="34,34 34,46 48,40" fill="#E8EBF0"/>
  </svg>
);

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console so DevTools / Netlify function logs capture it
    console.error("[CC] Unhandled render error:", error, errorInfo?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { error, errorInfo } = this.state;
    const isDev = process.env.NODE_ENV === "development";

    return (
      <div style={{
        minHeight: "100vh",
        background: "#080C12",
        backgroundImage: "linear-gradient(#0D1825 1px,transparent 1px),linear-gradient(90deg,#0D1825 1px,transparent 1px)",
        backgroundSize: "48px 48px",
        fontFamily: "Inter,system-ui,sans-serif",
        color: "#E8EBF0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}>
        <div style={{ maxWidth: 520, width: "100%", textAlign: "center" }}>

          {/* Top accent */}
          <div style={{ height: 3, background: "#D93025", borderRadius: "2px 2px 0 0", marginBottom: 0 }}/>

          <div style={{
            background: "#0D1825",
            border: "1px solid #1A2940",
            borderTop: "none",
            borderRadius: "0 0 8px 8px",
            padding: "40px 32px 32px",
          }}>
            <LogoMark />

            <div style={{
              fontFamily: "Impact,'Arial Black',sans-serif",
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#5A6A7E",
              marginTop: 12,
              marginBottom: 4,
            }}>
              Creator Commandos
            </div>

            <div style={{
              fontFamily: "Impact,'Arial Black',sans-serif",
              fontSize: 28,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#D93025",
              marginBottom: 8,
            }}>
              Mission Interrupted
            </div>

            <p style={{ fontSize: 14, color: "#8899AA", lineHeight: 1.7, marginBottom: 28 }}>
              Something went wrong on our end. Your data is safe — this is a display error,
              not a data error. Reloading the page usually fixes it.
            </p>

            {/* Error detail — dev only */}
            {isDev && error && (
              <div style={{
                background: "rgba(217,48,37,0.08)",
                border: "1px solid rgba(217,48,37,0.3)",
                borderRadius: 6,
                padding: "12px 16px",
                marginBottom: 24,
                textAlign: "left",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#D93025", marginBottom: 6 }}>
                  Dev — Error Detail
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", fontFamily: "monospace", wordBreak: "break-all" }}>
                  {error.toString()}
                </div>
                {errorInfo?.componentStack && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ fontSize: 11, color: "#5A6A7E", cursor: "pointer" }}>Component stack</summary>
                    <pre style={{ fontSize: 10, color: "#5A6A7E", marginTop: 6, whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto" }}>
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: "12px 28px",
                  background: "#C9A227",
                  border: "none",
                  borderRadius: 4,
                  color: "#080C12",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  cursor: "pointer",
                }}
              >
                ↺ Reload App
              </button>

              <button
                onClick={this.handleReset}
                style={{
                  padding: "12px 28px",
                  background: "none",
                  border: "1px solid #1A2940",
                  borderRadius: 4,
                  color: "#5A6A7E",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  cursor: "pointer",
                }}
              >
                Try Without Reload
              </button>
            </div>

            <p style={{ fontSize: 12, color: "#3A4A5E", marginTop: 24, lineHeight: 1.6 }}>
              If this keeps happening, email us at{" "}
              <a href="mailto:hello@creatorcommandos.app" style={{ color: "#C9A227" }}>
                hello@creatorcommandos.app
              </a>
            </p>
          </div>

          {/* Bottom accent */}
          <div style={{ height: 3, background: "#C9A227", borderRadius: "0 0 2px 2px" }}/>
        </div>
      </div>
    );
  }
}
